import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRecordLinksInNewTab } from "@dl/web-core-js/ui";

/**
 * The one way a row inside a record opens another record.
 *
 * Rows used to navigate when clicked anywhere, which made every stray click a
 * trip: you clicked a BOM line to look at it and were on another part, on its
 * first tab, with Back as the only way home. Inside a record, rows no longer
 * navigate; this button does, it says where it goes, and it opens the target
 * on the tab that matters (a component's own BOM, a parent's BOM). It is a real
 * link, so a middle-click or Ctrl-click opens it in a new tab instead; with
 * the account preference "Record links" on, every one does.
 *
 * List pages — Parts, Purchase orders — still open a row on click: that is
 * what a list is for.
 */

type Target =
  | { kind: "part"; id: number; tab?: string }
  | { kind: "production-order"; id: number; tab?: string }
  | { kind: "purchase-order"; id: number; tab?: string }
  | { kind: "supplier"; id: number; tab?: string }
  | { kind: "cut-job"; id: number; tab?: string };

export function OpenRecord({ target, label }: { target: Target; label: string }) {
  const { t } = useTranslation();
  const title = t("open.record", "Open {{label}}", { label });
  const className =
    "inline-flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunk hover:text-accent focus-visible:outline-2 focus-visible:outline-accent";
  const search = target.tab ? { tab: target.tab } : {};
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  const newTab = useRecordLinksInNewTab();
  const opens = newTab ? { target: "_blank", rel: "noopener" } : {};
  switch (target.kind) {
    case "part":
      return (
        <Link to="/manufacturing/parts/$partId" params={{ partId: String(target.id) }} search={search} onClick={stop} {...opens} className={className} title={title} aria-label={title}>
          <ArrowUpRight className="size-4" />
        </Link>
      );
    case "production-order":
      return (
        <Link to="/production/orders/$orderId" params={{ orderId: String(target.id) }} search={search} onClick={stop} {...opens} className={className} title={title} aria-label={title}>
          <ArrowUpRight className="size-4" />
        </Link>
      );
    case "supplier":
      return (
        <Link to="/manufacturing/suppliers/$supplierId" params={{ supplierId: String(target.id) }} search={search} onClick={stop} {...opens} className={className} title={title} aria-label={title}>
          <ArrowUpRight className="size-4" />
        </Link>
      );
    case "cut-job":
      return (
        <Link to="/production/cut-jobs/$jobId" params={{ jobId: String(target.id) }} onClick={stop} {...opens} className={className} title={title} aria-label={title}>
          <ArrowUpRight className="size-4" />
        </Link>
      );
    case "purchase-order":
      return (
        <Link to="/purchasing/purchase-orders/$orderId" params={{ orderId: String(target.id) }} search={search} onClick={stop} {...opens} className={className} title={title} aria-label={title}>
          <ArrowUpRight className="size-4" />
        </Link>
      );
  }
}
